import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Search, StopCircle, TrendingUp, AlertCircle, ChevronRight, BarChart2, X } from 'lucide-react';
import BacktestChart from './BacktestChart';

const getBaseUrl = () => {
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const url = isLocal ? 'http://localhost:5000' : window.location.origin;
    console.log(`[BaseURL] Current: ${window.location.hostname}, Chosen: ${url}`);
    return url;
};

// ---------- Styled Components ----------
const Page = styled.div`
  display: flex;
  height: calc(100vh - 64px);
  overflow: hidden;
  background: #141414;
  color: #fff;
  font-family: 'Inter', sans-serif;
`;

const LeftPanel = styled.div`
  width: ${p => p.$hasChart ? '420px' : '100%'};
  min-width: 380px;
  border-right: 1px solid #2a2a2a;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: width 0.3s ease;
`;

const RightPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #1a1a1a;
`;

const Header = styled.div`
  padding: 16px;
  border-bottom: 1px solid #2a2a2a;
  background: #1f1f1f;
`;

const Title = styled.h2`
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #fff;
`;

const ControlRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
`;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-bottom: 12px;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Label = styled.div`
  font-size: 11px;
  color: #999;
  font-weight: 500;
`;

const Select = styled.select`
  flex: 1;
  min-width: 0;
  box-sizing: border-box;
  background: #141414;
  border: 1px solid #333;
  color: #fff;
  padding: 8px 10px;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-size: 14px;
  padding-right: 32px;
  transition: all 0.2s;
  &:hover { border-color: #555; }
  &:focus { border-color: #e50914; outline: none; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const ScanButton = styled.button`
  background: ${p => p.$stop ? '#7f1d1d' : '#e50914'};
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  transition: all 0.2s;
  &:hover { opacity: 0.85; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const ProgressBar = styled.div`
  background: #141414;
  border-radius: 8px;
  height: 6px;
  margin-top: 10px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #e50914, #f59e0b);
  width: ${p => p.$pct}%;
  transition: width 0.3s ease;
  border-radius: 8px;
`;

const ProgressText = styled.div`
  font-size: 11px;
  color: #888;
  margin-top: 5px;
  display: flex;
  justify-content: space-between;
`;

const ResultsHeader = styled.div`
  padding: 10px 16px;
  font-size: 12px;
  color: #888;
  border-bottom: 1px solid #2a2a2a;
  display: flex;
  justify-content: space-between;
  background: #1f1f1f;
`;

const spin = keyframes`0%{transform:rotate(0deg)}100%{transform:rotate(360deg)}`;

const ResultList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px;
`;

const HitItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 8px;
  background: ${p => p.$active ? '#1a2a1a' : '#1f1f1f'};
  border: 1px solid ${p => p.$active ? '#22c55e55' : '#2a2a2a'};
  margin-bottom: 6px;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { background: #252525; border-color: #444; }
`;

const HitBadge = styled.div`
  background: #14532d;
  color: #22c55e;
  font-size: 10px;
  font-weight: 700;
  padding: 3px 7px;
  border-radius: 4px;
  flex-shrink: 0;
`;

const HitInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const HitName = styled.div`
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const HitMeta = styled.div`
  font-size: 11px;
  color: #888;
  margin-top: 2px;
`;

const HitReturn = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${p => parseFloat(p.$val) >= 0 ? '#22c55e' : '#ef4444'};
  flex-shrink: 0;
`;

const SkipItem = styled.div`
  font-size: 11px;
  color: #555;
  padding: 4px 6px;
  display: flex;
  gap: 6px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 10px;
  color: #555;
  font-size: 14px;
`;

// ---------- Scheduler Styled Components ----------
const SchedulerCard = styled.div`
  background: #1f1f1f;
  border-radius: 12px;
  padding: 16px;
  margin: 16px;
  border: 1px solid #2a2a2a;
  box-shadow: 0 4px 20px rgba(0,0,0,0.4);
`;

const SchedulerTitle = styled.h3`
  font-size: 14px;
  font-weight: 700;
  margin: 0 0 16px 0;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FormGroup = styled.div`
  margin-bottom: 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const TimeGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 16px;
`;

const ActionButton = styled.button`
  width: 100%;
  background: ${p => p.$stop ? '#cc1923' : '#22c55e'};
  color: #fff;
  border: none;
  padding: 10px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s;
  &:hover { opacity: 0.9; transform: translateY(-1px); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const LogArea = styled.div`
  margin-top: 12px;
  background: #141414;
  border-radius: 6px;
  padding: 8px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  color: #aaa;
  max-height: 100px;
  overflow-y: auto;
  border: 1px solid #333;
`;

const StatusBadge = styled.span`
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 10px;
  background: ${p => p.$active ? '#14532d' : '#2a2a2a'};
  color: ${p => p.$active ? '#22c55e' : '#888'};
  font-weight: 700;
`;

// ---------- Main Component ----------
export default function ScreenerPage() {
    const [strategies, setStrategies] = useState([]);
    const [selectedStrategy, setSelectedStrategy] = useState('');
    const [loadingStrategies, setLoadingStrategies] = useState(true);
    const [strategyError, setStrategyError] = useState(null);

    // Scheduler State
    const [schedConfig, setSchedConfig] = useState({
        interval: 60,
        startTime: '09:00',
        endTime: '15:30',
        strategyId: '',
        runNow: true,
        filters: {
            market: 'ALL',
            marketCapRange: 'ALL'
        }
    });
    const [schedStatus, setSchedStatus] = useState({
        isRunning: false,
        logs: []
    });
    const [isSaving, setIsSaving] = useState(false);

    const [isScanning, setIsScanning] = useState(false);
    const [progress, setProgress] = useState({ current: 0, total: 0, symbol: '', name: '', fetching: false });
    const [hits, setHits] = useState([]);
    const [skips, setSkips] = useState([]);
    const [selectedHit, setSelectedHit] = useState(null);
    const [summary, setSummary] = useState(null);
    const [sortBy, setSortBy] = useState('return'); // 'return' or 'winRate'
    const abortRef = useRef(null);
    const [marketFilter, setMarketFilter] = useState('ALL');
    const [capFilter, setCapFilter] = useState('ALL');

    const handleColorChange = async (indId, newColor) => {
        const selectedCustom = strategies.find(s => s.id === selectedStrategy);
        if (!selectedCustom) return;

        const updatedIndicators = (selectedCustom.indicators || []).map(ind => 
            ind.id === indId ? { ...ind, color: newColor } : ind
        );
        const updatedStrategy = { ...selectedCustom, indicators: updatedIndicators, updatedAt: new Date().toISOString() };
        
        setStrategies(prev => prev.map(s => s.id === selectedStrategy ? updatedStrategy : s));

        if (selectedHit && selectedHit.chartData) {
            const chartData = selectedHit.chartData;
            if (chartData.indicatorDefs) {
                chartData.indicatorDefs = chartData.indicatorDefs.map(def => 
                    def.id === indId ? { ...def, color: newColor } : def
                );
            }
            setSelectedHit({ ...selectedHit }); 
        }

        try {
            await axios.post(`${getBaseUrl()}/api/stock/backtest/strategies`, updatedStrategy);
        } catch (err) {
            console.error('Failed to save color preference:', err);
        }
    };

    // Default to today
    const getTodayStr = () => {
        const d = new Date();
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    };
    const [targetDate, setTargetDate] = useState(getTodayStr());

    // Load strategies
    useEffect(() => {
        setLoadingStrategies(true);
        setStrategyError(null);
        axios.get(`${getBaseUrl()}/api/stock/backtest/strategies`)
            .then(r => {
                const data = r.data || [];
                setStrategies(data);
                if (data.length > 0) {
                    setSelectedStrategy(data[0].id);
                }
                setLoadingStrategies(false);
            })
            .catch(e => {
                console.error('전략 조회 실패:', e);
                setStrategyError(e.message);
                setLoadingStrategies(false);
            });
    }, []);

    const fetchSchedulerStatus = async (isInitial = false) => {
        try {
            const res = await axios.get(`${getBaseUrl()}/api/batch/screener/scheduler/status`);
            setSchedStatus({
                isRunning: res.data.isRunning,
                logs: res.data.logs
            });
            if (isInitial || res.data.isRunning) {
                setSchedConfig(res.data.config);
            }
        } catch (err) {
            console.error('Failed to fetch scheduler status:', err);
        }
    };

    const toggleScheduler = async () => {
        setIsSaving(true);
        try {
            if (schedStatus.isRunning) {
                await axios.post(`${getBaseUrl()}/api/batch/screener/scheduler/stop`);
            } else {
                await axios.post(`${getBaseUrl()}/api/batch/screener/scheduler/config`, {
                    ...schedConfig,
                    strategyId: selectedStrategy,
                    filters: {
                        market: marketFilter,
                        marketCapRange: capFilter
                    },
                    enabled: true,
                    immediate: schedConfig.runNow
                });
            }
            await fetchSchedulerStatus();
        } catch (err) {
            console.error('Scheduler toggle failed:', err);
            alert('스케쥴러 제어에 실패했습니다.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleTimeChange = (e, field) => {
        let val = e.target.value.replace(/[^0-9:]/g, '').substring(0, 5);
        if (val.length === 2 && !val.includes(':') && (e.nativeEvent && e.nativeEvent.inputType !== 'deleteContentBackward')) {
            val += ':';
        }
        setSchedConfig({...schedConfig, [field]: val});
    };

    useEffect(() => {
        fetchSchedulerStatus(true);
        const interval = setInterval(() => {
            fetchSchedulerStatus();
        }, 60000); 
        return () => clearInterval(interval);
    }, []);

    const handleScan = () => {
        if (!selectedStrategy) return;
        setHits([]);
        setSkips([]);
        setSummary(null);
        setSelectedHit(null);
        setProgress({ current: 0, total: 0, symbol: '', name: '', fetching: false });
        setIsScanning(true);

        // Use fetch + ReadableStream for SSE via POST
        const ctrl = new AbortController();
        abortRef.current = ctrl;

        fetch(`${getBaseUrl()}/api/batch/screener/run`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                strategyId: selectedStrategy,
                targetDate,
                filters: {
                    market: marketFilter,
                    marketCapRange: capFilter
                }
            }),
            signal: ctrl.signal,
        }).then(async (response) => {
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop(); // last incomplete line

                for (const line of lines) {
                    if (!line.startsWith('data:')) continue;
                    try {
                        const evt = JSON.parse(line.slice(5).trim());
                        if (evt.type === 'progress') {
                            setProgress({ current: evt.current, total: evt.total, symbol: evt.symbol, name: evt.name, fetching: !!evt.fetching });
                        } else if (evt.type === 'hit') {
                            setHits(prev => [...prev, evt]);
                        } else if (evt.type === 'skip') {
                            setSkips(prev => [...prev, evt]);
                        } else if (evt.type === 'done') {
                            setSummary(evt);
                            setIsScanning(false);
                        } else if (evt.type === 'error') {
                            console.error('[Screener] Server error:', evt.message);
                            setIsScanning(false);
                        }
                    } catch (_) { }
                }
            }
        }).catch(e => {
            if (e.name !== 'AbortError') console.error('[Screener] Fetch error:', e);
            setIsScanning(false);
        });
    };

    const handleStop = () => {
        abortRef.current?.abort();
        setIsScanning(false);
    };

    const pct = progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0;

    // Derived sorted hits
    const sortedHits = [...hits].sort((a, b) => {
        if (sortBy === 'return') {
            const retA = parseFloat(a.totalReturn);
            const retB = parseFloat(b.totalReturn);
            if (retB !== retA) return retB - retA;
            return parseFloat(b.winRate) - parseFloat(a.winRate);
        } else {
            const wrA = parseFloat(a.winRate);
            const wrB = parseFloat(b.winRate);
            if (wrB !== wrA) return wrB - wrA;
            return parseFloat(b.totalReturn) - parseFloat(a.totalReturn);
        }
    });

    return (
        <Page>
            <LeftPanel $hasChart={!!selectedHit}>
                <Header>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', marginBottom: '12px' }}>
                        <Title style={{ margin: 0 }}><Search size={18} color="#e50914" /> 전략 스크리너</Title>
                        <Link to="/labs/screener/history" style={{ color: '#aaa', fontSize: '13px', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <BarChart2 size={14} /> 히스토리 보기
                        </Link>
                    </div>
                    
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                        <Select 
                            value={selectedStrategy} 
                            onChange={e => setSelectedStrategy(e.target.value)} 
                            disabled={isScanning || loadingStrategies}
                            style={{ flex: 1 }}
                        >
                            {loadingStrategies && <option value="">전략 목록을 불러오는 중...</option>}
                            {strategyError && <option value="">오류: {strategyError}</option>}
                            {!loadingStrategies && strategies.length === 0 && <option value="">저장된 전략이 없습니다</option>}
                            {strategies.map(s => (
                                <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                        </Select>
                        {isScanning ? (
                            <ScanButton $stop onClick={handleStop}>
                                <StopCircle size={14} /> 중지
                            </ScanButton>
                        ) : (
                            <ScanButton onClick={handleScan} disabled={!selectedStrategy || loadingStrategies}>
                                <Search size={14} /> 스캔
                            </ScanButton>
                        )}
                    </div>

                    <FilterGrid>
                        <FilterGroup>
                            <Label>기준일</Label>
                            <input
                                type="date"
                                value={targetDate}
                                onChange={(e) => setTargetDate(e.target.value)}
                                disabled={isScanning}
                                max={getTodayStr()}
                                style={{
                                    flex: 1, minWidth: 0,
                                    background: '#141414', border: '1px solid #333',
                                    color: '#fff', padding: '8px 10px',
                                    borderRadius: '6px', fontSize: '13px',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </FilterGroup>
                        <FilterGroup>
                            <Label>시장 구분</Label>
                            <Select value={marketFilter} onChange={(e) => setMarketFilter(e.target.value)} disabled={isScanning}>
                                <option value="ALL">전체 시장</option>
                                <option value="KOSPI">코스피 (KOSPI)</option>
                                <option value="KOSDAQ">코스닥 (KOSDAQ)</option>
                            </Select>
                        </FilterGroup>
                        <FilterGroup>
                            <Label>시가총액 범위</Label>
                            <Select value={capFilter} onChange={(e) => setCapFilter(e.target.value)} disabled={isScanning}>
                                <option value="ALL">전체 시총</option>
                                <option value="UNDER_500B">5,000억 이하</option>
                                <option value="500B_2T">5,000억 ~ 2조</option>
                                <option value="OVER_2T">2조 이상</option>
                            </Select>
                        </FilterGroup>
                    </FilterGrid>

                    {(isScanning || progress.total > 0) && (
                        <>
                            <ProgressBar>
                                <ProgressFill $pct={pct} />
                            </ProgressBar>
                            <ProgressText>
                                <span>
                                    {isScanning
                                        ? `${progress.fetching ? '🌐 API 조회 중: ' : '🔍 분석 중: '}${progress.name} (${progress.symbol})`
                                        : summary ? `✅ 완료 — 신호: ${summary.hits}개 / 스킵: ${summary.skipped}개` : ''}
                                </span>
                                <span>{progress.current}/{progress.total} ({pct}%)</span>
                            </ProgressText>
                        </>
                    )}
                </Header>

                <SchedulerCard>
                    <SchedulerTitle>
                        <TrendingUp size={16} color="#22c55e" /> 
                        스케쥴러 설정
                        <StatusBadge $active={schedStatus.isRunning}>
                            {schedStatus.isRunning ? 'RUNNING' : 'IDLE'}
                        </StatusBadge>
                    </SchedulerTitle>

                    <FormGroup>
                        <Label>작업 간격 (분)</Label>
                        <input 
                            type="number"
                            value={schedConfig.interval}
                            onChange={e => setSchedConfig({...schedConfig, interval: parseInt(e.target.value) || 0})}
                            style={{ background: '#141414', border: '1px solid #333', color: '#fff', padding: '8px', borderRadius: '4px', width: '100%', boxSizing: 'border-box' }}
                        />
                    </FormGroup>

                    <TimeGrid>
                        <FormGroup>
                            <Label>시작 시간</Label>
                            <input 
                                type="text"
                                placeholder="HH:mm"
                                maxLength="5"
                                value={schedConfig.startTime}
                                onChange={e => handleTimeChange(e, 'startTime')}
                                style={{ background: '#141414', border: '1px solid #333', color: '#fff', padding: '8px', borderRadius: '4px', width: '100%', boxSizing: 'border-box' }}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>종료 시간</Label>
                            <input 
                                type="text"
                                placeholder="HH:mm"
                                maxLength="5"
                                value={schedConfig.endTime}
                                onChange={e => handleTimeChange(e, 'endTime')}
                                style={{ background: '#141414', border: '1px solid #333', color: '#fff', padding: '8px', borderRadius: '4px', width: '100%', boxSizing: 'border-box' }}
                            />
                        </FormGroup>
                    </TimeGrid>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                        <input 
                            type="checkbox" 
                            id="runNow"
                            checked={schedConfig.runNow}
                            onChange={e => setSchedConfig({...schedConfig, runNow: e.target.checked})}
                            style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                        />
                        <Label htmlFor="runNow" style={{ margin: 0, cursor: 'pointer', color: '#fff' }}>저장 시 즉시 실행</Label>
                    </div>

                    <ActionButton 
                        onClick={toggleScheduler} 
                        disabled={isSaving}
                        $stop={schedStatus.isRunning}
                    >
                        {schedStatus.isRunning ? <StopCircle size={14} /> : <TrendingUp size={14} />}
                        {schedStatus.isRunning ? '작업 중지' : '작업 시작'}
                    </ActionButton>

                    {schedStatus.logs.length > 0 && (
                        <LogArea>
                            {schedStatus.logs.map((log, i) => (
                                <div key={i}>{log}</div>
                            ))}
                        </LogArea>
                    )}
                </SchedulerCard>

                <ResultsHeader>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span>📋 발견 종목 ({hits.length}개)</span>
                        {!isScanning && hits.length > 0 && (
                            <div style={{ display: 'flex', gap: '4px', marginLeft: '8px' }}>
                                <button
                                    onClick={() => setSortBy('return')}
                                    style={{
                                        fontSize: '10px',
                                        padding: '2px 6px',
                                        background: sortBy === 'return' ? '#e50914' : '#333',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    수익순
                                </button>
                                <button
                                    onClick={() => setSortBy('winRate')}
                                    style={{
                                        fontSize: '10px',
                                        padding: '2px 6px',
                                        background: sortBy === 'winRate' ? '#e50914' : '#333',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    승률순
                                </button>
                            </div>
                        )}
                    </div>
                    {skips.length > 0 && <span style={{ color: '#555' }}>스킵 {skips.length}개</span>}
                </ResultsHeader>

                <ResultList>
                    {hits.length === 0 && !isScanning && (
                        <EmptyState>
                            <BarChart2 size={40} color="#333" />
                            <span>스캔 버튼을 눌러 시작하세요</span>
                        </EmptyState>
                    )}

                    {sortedHits.map((hit, i) => (
                        <HitItem key={`${hit.symbol}-${i}`} $active={selectedHit?.symbol === hit.symbol} onClick={() => setSelectedHit(hit)}>
                            <HitBadge>BUY</HitBadge>
                            <HitInfo>
                                <HitName>{hit.name}</HitName>
                                <HitMeta>
                                    {hit.symbol} · 신호일: {String(hit.signalDate).replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3')}
                                    · 승률: {hit.winRate}% ({hit.totalTrades}회)
                                </HitMeta>
                            </HitInfo>
                            <HitReturn $val={hit.totalReturn}>
                                {parseFloat(hit.totalReturn) >= 0 ? '+' : ''}{hit.totalReturn}%
                            </HitReturn>
                            <ChevronRight size={14} color="#555" />
                        </HitItem>
                    ))}

                    {isScanning && hits.length === 0 && (
                        <EmptyState>
                            <div style={{ fontSize: 12, color: '#555' }}>스캔 중... 신호 종목이 발견되면 즉시 표시됩니다</div>
                        </EmptyState>
                    )}
                </ResultList>
            </LeftPanel>

            {selectedHit && (
                <RightPanel>
                    <ChartHeader>
                        <div>
                            <span style={{ fontWeight: 700, fontSize: 16 }}>{selectedHit.name}</span>
                            <span style={{ color: '#888', fontSize: 13, marginLeft: 8 }}>({selectedHit.symbol})</span>
                            <span style={{ marginLeft: 12, color: '#22c55e', fontSize: 13 }}>
                                백테스트 수익: {parseFloat(selectedHit.totalReturn) >= 0 ? '+' : ''}{selectedHit.totalReturn}%
                            </span>
                        </div>
                        <CloseBtn onClick={() => setSelectedHit(null)}><X size={18} /></CloseBtn>
                    </ChartHeader>
                    <div style={{ flex: 1, overflow: 'hidden' }}>
                        <BacktestChart chartData={selectedHit.chartData} onColorChange={handleColorChange} />
                    </div>
                </RightPanel>
            )}
        </Page>
    );
}
